import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, X } from 'lucide-react';

/**
 * HostDashboardQrButton
 * ---------------------------------------------------------------------------
 * Header pill that exposes a QR code linking to /admin/host-dashboard so the
 * host can scan once with their phone and open the duplicate dashboard view.
 *
 * Design constraints (host-stated):
 *   - Must NOT shift other header elements. Renders as a fixed 40x40 button —
 *     same dimensions as the existing Help / Notifications buttons.
 *   - Must NOT be intrusive. Default state: tiny icon. Click reveals popover.
 *
 * The popover is absolutely positioned so it does not affect header layout.
 */
const HostDashboardQrButton = () => {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const url = `${window.location.origin}/admin/host-dashboard`;

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        data-testid="header-host-qr-btn"
        onClick={() => setOpen(v => !v)}
        title="Host Dashboard QR — scan with your phone"
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 10,
          border: '1px solid rgba(185, 139, 31, 0.25)',
          background: open ? 'rgba(185, 139, 31, 0.18)' : 'rgba(255,255,255,0.06)',
          cursor: 'pointer',
          transition: 'all 200ms ease',
        }}
      >
        <Smartphone size={17} color="rgba(255,255,255,0.6)" />
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="host-qr-popover"
          style={{
            position: 'absolute',
            top: 48,
            right: 0,
            width: 240,
            padding: 16,
            background: 'rgba(30,26,20,0.96)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(198,167,90,0.30)',
            borderRadius: 14,
            boxShadow: '0 20px 60px rgba(0,0,0,0.40)',
            zIndex: 200,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#F5F0E8', letterSpacing: '0.4px' }}>
              Host Dashboard
            </span>
            <button
              onClick={() => setOpen(false)}
              data-testid="host-qr-close-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
            >
              <X size={14} color="rgba(255,255,255,0.55)" />
            </button>
          </div>

          <div style={{
            background: '#FFFFFF',
            borderRadius: 8,
            padding: 12,
            display: 'flex',
            justifyContent: 'center',
          }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(url)}`}
              alt="Host Dashboard QR Code"
              width={180}
              height={180}
              style={{ display: 'block' }}
            />
          </div>

          <p style={{
            fontSize: 11,
            color: 'rgba(245,240,232,0.70)',
            margin: '12px 0 0',
            lineHeight: 1.45,
            textAlign: 'center',
          }}>
            Scan with your phone. Sign in once on your phone&apos;s admin page; this link
            opens the same dashboard view afterward.
          </p>

          <div style={{
            marginTop: 10,
            padding: '6px 8px',
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid rgba(198,167,90,0.15)',
            borderRadius: 6,
            fontSize: 10,
            color: 'rgba(245,240,232,0.55)',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            wordBreak: 'break-all',
            textAlign: 'center',
          }}>
            {url}
          </div>
        </div>
      )}
    </div>
  );
};

export default HostDashboardQrButton;
